<template>
  <div class="relative">
    <select
      v-if="computedType === 'select'"
      v-model="computedValue"
      :name="name"
      :id="id"
      :required="required"
      :class="inputElClass">
      <option :value="''" disabled selected><span style="dark:placeholder-gray-400"> {{ placeholder }}</span></option>
      <option v-for="option in options" :key="option.id ?? option" :value="option.name ?? option" :selected="option.id === '6a23c1e2-74c8-4fa4-a775-25bf14178adb'">{{ option.label ?? option.name ?? option }}</option>
    </select>
    <textarea
      v-else-if="computedType === 'textarea'"
      v-model="computedValue"
      :class="inputElClass"
      :name="name"
      :id="id"
      :placeholder="placeholder"
      :required="required"
    ></textarea>
    <input
      v-else
      ref="inputEl"
      :value="modelValue"
@input="$emit('update:modelValue', $event.target.value)"
      :name="name"
      :autocomplete="autocomplete"
      :required="required"
      :disabled="disabled"
      :id="id"
      :placeholder="placeholder"
      :readonly="readonly"
      :type="computedType"
      :class="inputElClass"
    >
    <control-icon
      v-if="icon"
      :icon="icon"
      :h="controlIconH"
    />
  </div>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import ControlIcon from '@/components/ControlIcon'

export default {
  name: 'ControlName',
  components: {
    ControlIcon
  },
  props: {
    name: String,
    id: String,
    required: Boolean,
    disabled: Boolean,
    autocomplete: String,
    placeholder: String,
    readonly: String,
    icon: String,
    options: Array,
    type: {
      type: String,
      default: 'text'
    },
    modelValue: {
      type: [String, Number, Boolean, Array, Object],
      default: ''
    },
    borderless: Boolean,
    transparent: Boolean,
    hasError: Boolean,
    hasSuccess: Boolean,
    ctrlKFocus: Boolean
  },
  emits: ['update:modelValue', 'right-icon-click'],
  setup (props, { emit }) {
    const computedValue = computed({
      get: () => props.modelValue,
      set: value => {
        emit('update:modelValue', value)
      }
    })

    const inputElClass = computed(() => {
      const base = [
        'px-3 py-2 max-w-full focus:ring focus:outline-none  rounded w-full',
        'dark:placeholder-gray-400',
        computedType.value === 'textarea' ? 'h-24' : 'h-12',
        props.borderless ? 'border-0' : 'border',
        props.transparent ? 'bg-transparent' : 'bg-white dark:bg-gray-800',
        props.hasError ? 'border-red-500' : (props.hasSuccess ? 'border-green-500' : 'border-gray-500')
      ]

      if (props.icon) {
        base.push('pl-10')
      }

      return base
    })

    const computedType = computed(() => props.options ? 'select' : props.type)

    const controlIconH = computed(() => props.type === 'textarea' ? 'h-full' : 'h-12')

    const store = useStore()

    const inputEl = ref(null)

    if (props.ctrlKFocus) {
      const fieldFocusHook = e => {
        if (e.ctrlKey && e.key === 'k') {
          e.preventDefault()
          inputEl.value.focus()
        } else if (e.key === 'Escape') {
          inputEl.value.blur()
        }
      }

      onMounted(() => {
        if (!store.state.isFieldFocusRegistered) {
          window.addEventListener('keydown', fieldFocusHook)

          store.commit('basic', {
            key: 'isFieldFocusRegistered',
            value: true
          })
        } else {
          console.error('Duplicate field focus event')
        }
      })

      onBeforeUnmount(() => {
        window.removeEventListener('keydown', fieldFocusHook)

        store.commit('basic', {
          key: 'isFieldFocusRegistered',
          value: false
        })
      })
    }

    return {
      computedValue,
      inputElClass,
      computedType,
      controlIconH,
      inputEl
    }
  }
}
</script>
